import { Injectable } from '@angular/core';
import { AssetColorLabel, AssetDto, AssetStatus, CommentPosition, GalleryDto } from '@picflow/types';
import { firstValueFrom } from 'rxjs';
import { AssetFeedback } from '../gallery-shared/interfaces/asset-feedback';
import { Shape } from '../gallery-shared/types/shapes';
import { AnalyticsService } from './analytics.service';
import { AssetService } from './asset.service';
import { CommentService } from './comment.service';
import { DataLoaderService } from './data-loader.service';
import { GuestAuthService } from './guest-auth.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';
import { UserService } from './user.service';
import { WatcherService } from './watcher.service';

@Injectable({
  providedIn: 'root',
})
export class AssetFeedbackService {
  showedAnonymousInfo = false;
  constructor(
    private assetService: AssetService,
    private watcherService: WatcherService,
    private loader: DataLoaderService,
    private state: StateService,
    private userService: UserService,
    private guestAuthService: GuestAuthService,
    private commentService: CommentService,
    private storage: StorageService,
    private analytics: AnalyticsService,
    private toasts: ToastService
  ) {}

  async updateAssetStatus(asset: AssetDto, gallery: GalleryDto, status: AssetStatus) {
    if (!gallery.enabled_feedback_options.includes('flag')) return;
    if (!(await this.checkAuth(gallery, { type: 'status', content: status }))) return;
    asset.status = status;
    await this.assetService.updateAsset(asset.id, { status }, gallery.id, <string>asset.section);
    this.afterFeedback();
    this.watcherService.watchAsset(asset.id, gallery.id);
  }

  async updateColorLabel(asset: AssetDto, gallery: GalleryDto, color_label: AssetColorLabel) {
    if (!gallery.enabled_feedback_options.includes('color_label')) return;
    if (!(await this.checkAuth(gallery, { type: 'color_label', content: color_label }))) return;
    asset.color_label = color_label;
    await this.assetService.updateAsset(asset.id, { color_label }, gallery.id, <string>asset.section);
    this.afterFeedback();
    this.watcherService.watchAsset(asset.id, gallery.id);
  }

  async toggleLike(asset: AssetDto, gallery: GalleryDto) {
    if (!gallery.enabled_feedback_options.includes('favourite')) return;
    const wasLoggedIn = !!this.state.guestAuthToken || !!this.state.user;
    if (!(await this.checkAuth(gallery, { type: 'like' }))) return;

    let _asset = asset;
    if (!wasLoggedIn) {
      this.loader.removeFuzzy('assets_');
      _asset = await this.assetService.getAsset(asset.id, gallery.id, { noCache: true });
      asset['is_liked'] = _asset['is_liked'];
    }

    if (_asset['is_liked'] && !wasLoggedIn) return; // was already liked

    if (_asset['is_liked']) {
      asset['is_liked'] = false;
      asset.likes -= 1;
      await this.assetService.unlikeAsset(asset.id, gallery.id);
    } else {
      asset['is_liked'] = true;
      asset.likes += 1;
      await this.assetService.likeAsset(asset.id, gallery.id);
    }
    this.afterFeedback();
    this.watcherService.watchAsset(asset.id, gallery.id);
  }

  async sendComment(
    asset: AssetDto,
    gallery: GalleryDto,
    message: string,
    annotation: Shape[],
    position?: CommentPosition
  ) {
    this.storage.set('annotation', annotation, true);
    if (!(await this.checkAuth(gallery, { type: 'comment', content: message }))) return;
    if (!message && !annotation.length) return;
    if (message) {
      await firstValueFrom(this.commentService.postComment(asset.id, message, gallery.id, position));
      this.analytics.track('asset_comment_added');
    }
    if (annotation.length) {
      await firstValueFrom(this.commentService.postAnnotation(asset, annotation, gallery.id, position));
      this.analytics.track('asset_annotation_added');
    }
    this.afterFeedback();
    this.watcherService.watchAsset(asset.id, gallery.id);
  }

  public async checkAuth(gallery: GalleryDto, feedback?: AssetFeedback) {
    // Check if identified
    const guestLoggedIn = !!this.state.guestAuthToken;
    const userLoggedIn = !!(await this.userService.getUser());

    if (!guestLoggedIn && !userLoggedIn) {
      const resp = await this.guestAuthService.authenticateForFeedback(gallery, feedback);
      if (!resp) return;
    }
    return true;
  }

  private afterFeedback() {
    if (this.state.guest && !this.state.guest.email) {
      if (this.showedAnonymousInfo) return;
      this.showedAnonymousInfo = true;
      this.toasts.add(`You left feedback anonymously as <b>${this.state.guest.name}</b>.`, 'success', {
        duration: 5000,
      });
    }
  }
}
